import {recordZoom} from '../../../services/form/form';
import {Field, fieldValue} from '../../../stores/Form/Field';
import {Form} from '../../../stores/Form/Form';
import {Record} from '../../../stores/Form/Record';
import {Screen, APIActionStatus} from '../../../stores/Form/Screen';
import {closeFormBySession, openForm} from './form';
import {addAndApplyFilters, formatFilterValue} from './filters';
import {addNewScreen, callLinkedActions} from './screen';
import {isNewApiForm, runForm} from '../../../_shared/GWTBridge/invoke/netForm';
import {apiLocker, Column, formsStore} from '../../../stores/Form';

export const isZoomable = (column: Column) => {
    const form = column.parentForm;

    return (
        form.ename !== column.Zoomfname &&
        (!column.Relcol || column.parentForm.ename !== column.relatedIdColumn.Zoomfname)
    );
};

export const zoom = async (sourceForm: Form, column: Column) => {
    if (sourceForm.searchModeLevel > 0) {
        return null;
    }

    apiLocker.lockSession(sourceForm.session, {loaderTimeout: 0});
    // regular case
    let targetForm: Form | null = null;
    if (column.Zoomfname) {
        const filterValue = getZoomFilterValue(sourceForm, column.id);
        if (isNewApiForm(column.Zoomfname)) {
            targetForm = await zoomFetch(column.Zoomfname, filterValue, column);
        } else {
            runForm(column.Zoomfname, Number(column.Column), column.Name, filterValue, false);
        }
    }
    // zooming by related field
    else if (column.Relcol) {
        const zoomColumn = sourceForm.columnsMap.get(Number(column.Relcol));
        const filterValue = getZoomFilterValue(sourceForm, zoomColumn.id);
        if (isNewApiForm(zoomColumn.Zoomfname)) {
            targetForm = await zoomFetch(zoomColumn.Zoomfname, filterValue, zoomColumn);
        } else {
            runForm(
                zoomColumn.Zoomfname,
                Number(zoomColumn.Column),
                zoomColumn.Name,
                filterValue,
                false
            );
        }
    }
    // dynamic zoom
    else {
        const zoomFormName = await getZoomFormNameFromServer(sourceForm, column.id);
        if (zoomFormName) {
            const filterValue = getZoomFilterValue(sourceForm, column.id);
            if (isNewApiForm(zoomFormName)) {
                targetForm = await zoomFetch(zoomFormName, filterValue, column);
            } else {
                runForm(zoomFormName, Number(column.Column), column.Name, filterValue, false);
            }
        }
    }
    if (targetForm) {
        const currentScreen = formsStore.getScreenBySession(sourceForm.session);
        if (currentScreen) {
            // close record dialog when zooming to form
            currentScreen.setRecordDialog(false);
        }
        addNewScreen(targetForm, formsStore.getScreenBySession(sourceForm.session));
    }

    apiLocker.unlockSession(sourceForm.session);
    return targetForm;
};

export const actionZoom = async (sourceForm: Form, zoomFormName: string) => {
    apiLocker.lockSession(sourceForm.session, {loaderTimeout: 0});
    const filterColumn = Number(sourceForm.currentColumn.Relcol)
        ? sourceForm.currentColumn.relatedIdColumn
        : sourceForm.currentColumn;
    const filterValue = getZoomFilterValue(sourceForm, filterColumn.id);
    const targetForm = await zoomFetch(zoomFormName, filterValue, filterColumn);
    addNewScreen(targetForm, formsStore.getScreenBySession(sourceForm.session));
    apiLocker.unlockSession(sourceForm.session);
};

export const zoomFetch = async (
    zoomFormName: string,
    filterValue: string,
    zoomColumn?: Column
): Promise<Form | null> => {
    const targetForm: Form | null = await openForm(zoomFormName, !filterValue);

    if (targetForm) {
        const zoomField =
            zoomColumn?.Column && targetForm.columns.find(c => c.Column === zoomColumn.Column);
        // if no zoomColumn provided (ex.: opening a form from recent updates),
        // use `form.ZoomField` as a target zoom field
        const zoomFieldId = zoomField?.id || Number(targetForm.form.ZoomField);
        if (filterValue && zoomFieldId) {
            const filter = {fieldId: zoomFieldId, value: filterValue};
            await addAndApplyFilters(targetForm, [filter]);
        }
        let targetFieldId = zoomFieldId;
        // if zoomField was found, but it's hidden
        // use zoomColumn.Name to find the target field
        // TODO: check with David how it was implemented in the BE
        // ex.: Customers => Customer Contacts => zoom from Name field
        if (zoomField?.Hidden) {
            targetFieldId = targetForm.columns.find(c => c.Name === zoomColumn.Name)?.id;
        }
        targetForm.currentColumnId = targetFieldId;

        return targetForm;
    }
    return null;
};

export const getZoomFormNameFromServer = async (form: Form, columnId: number) => {
    const {session, currentRow, records} = form;
    if (records?.length) {
        const activeRow = records[currentRow || 0];
        const res = await recordZoom(session, activeRow?.keys || []);
        if (res?.ColumnZoom) {
            return res.ColumnZoom[columnId];
        }
    }
    return null;
};

const getZoomFilterValue = (form: Form, columnId: number): string => {
    const value = form.records[form.currentRow]?.fieldsMap.get(columnId).currentValue;
    return formatFilterValue(form, columnId, value);
};

export const chooseCloseZoomForm = async (screen: Screen) => {
    return callLinkedActions(
        async () => await screen.checkAndSave(),
        async () => {
            const form: Form = screen.getActiveForm();
            const zoomFieldId = Number(form.form.ZoomField);
            const record: Record = form.records[form.currentRow];
            const zoomField: Field = record.getField(zoomFieldId);
            const value: fieldValue = zoomField.currentValue;
            if (value) {
                const sourceForm: Form = screen.zoomSource.getActiveForm();
                const sourceRecord: Record = sourceForm.records[sourceForm.currentRow];
                if (sourceForm.currentColumn.Relcol) {
                    sourceForm.currentColumnId = Number(sourceForm.currentColumn.Relcol);
                }
                const sourceField: Field = sourceRecord.getField(sourceForm.currentColumnId);
                sourceField.setCurrentValue(value);
                if (sourceForm.records.length === 1) {
                    sourceForm.insertEmptyRecord(sourceForm.records.length);
                }
            }
            await closeFormBySession(screen.session, false);
            return APIActionStatus.passed;
        }
    );
};
